import type { ThemeConfig } from 'antd';

/**
 * Bảng màu nhận diện MVA Lab, theo tài liệu "Quy định tạo hình ảnh truyền thông" (mục 3).
 *
 * Sáu màu gốc lấy đúng mã trong tài liệu. Các sắc nhạt và sắc đậm bên dưới chỉ là biến thể của sáu
 * màu đó, dùng cho nền ô, viền và chữ cần đủ tương phản — không thêm màu mới vào bộ nhận diện.
 *
 * Quy tắc dùng, cũng theo tài liệu:
 *
 * - Trắng và xanh dương là hai màu chủ đạo.
 * - Đen than và gold bổ trợ: đen than cho chữ, gold cho điểm nhấn nhỏ và mức "cần chú ý".
 * - Đỏ và xanh lá chỉ dùng khi mang nghĩa: đỏ là lỗi, quá hạn; xanh lá là đạt, hoàn thành.
 *
 * Màu nào cũng phải đi kèm chữ — không có trạng thái nào chỉ được nói bằng màu.
 */
export const BrandColors = {
  /** Nền chính, tạo độ sáng và khoảng thở. */
  white: '#FFFFFF',
  /** Màu nhận diện công nghệ: hành động chính, liên kết, mục đang chọn. */
  blue: '#1769D2',
  /** Xanh dương đậm — chữ trên nền xanh nhạt, trạng thái nhấn giữ. */
  blueDeep: '#0F4E9E',
  /** Xanh dương rất nhạt — nền mục menu đang chọn, nền cột kéo thả đang trỏ tới. */
  blueSoft: '#EAF2FC',
  /** Xanh dương gần trắng — nền vùng giới thiệu, dòng bảng khi rê chuột. */
  blueMist: '#F4F8FE',
  /** Đen than — chữ nội dung và con số. */
  ink: '#171B24',
  /** Chữ phụ trên nền trắng. */
  inkSecondary: '#4A5263',
  /** Chữ mờ: chú thích, mục đã ngừng. */
  inkMuted: '#7B8494',
  /** Gold — điểm nhấn cao cấp, đường viền nhỏ, mức "cần chú ý". */
  gold: '#C9A34A',
  /**
   * Gold đậm — khi gold phải làm CHỮ hoặc nền của chữ trắng. Gold gốc trên nền trắng chỉ đạt tương
   * phản khoảng 2,4:1, đọc không rõ; sắc này đạt khoảng 4,2:1.
   */
  goldDeep: '#9A7628',
  /** Gold rất nhạt — nền nhãn và vùng nhấn nhẹ. */
  goldSoft: '#FBF6EA',
  /** Đỏ — lỗi, quá hạn, mức cần xử lý ngay. */
  red: '#D93636',
  /** Xanh lá — đạt, hoàn thành, kết quả tích cực. */
  green: '#26965B',
  /** Nền khung ứng dụng: gần trắng, hơi lạnh — không dùng xám đục. */
  canvas: '#F5F7FB',
  /** Nền vùng phụ: tiêu đề bảng, thanh công cụ, ô trống chưa có ảnh. */
  surfaceSubtle: '#F7F9FC',
  /** Viền nhẹ giữa các vùng. */
  border: '#E6EBF2',
  /** Viền ô nhập và nút. */
  borderStrong: '#D5DCE6',
} as const;

/** Bộ font: một font sans-serif hiện đại, có đủ dấu tiếng Việt; phần sau là font dự phòng của hệ điều hành. */
const fontFamily =
  "'Inter Variable', Inter, 'Segoe UI', system-ui, -apple-system, Roboto, 'Helvetica Neue', Arial, sans-serif";

/**
 * Chủ đề của thư viện thành phần.
 *
 * Đặt màu ở đây thay vì tô tại từng trang: nút, bảng, thẻ, nhãn, hộp thoại và thông báo đều lấy màu
 * từ một chỗ, nên toàn hệ thống đổi theo cùng lúc và không có trang nào lạc tông.
 */
export const brandTheme: ThemeConfig = {
  token: {
    colorPrimary: BrandColors.blue,
    colorInfo: BrandColors.blue,
    colorLink: BrandColors.blue,
    colorSuccess: BrandColors.green,
    colorError: BrandColors.red,
    colorWarning: BrandColors.gold,

    colorTextBase: BrandColors.ink,
    colorBgLayout: BrandColors.canvas,
    colorBorder: BrandColors.borderStrong,
    colorBorderSecondary: BrandColors.border,
    colorFillAlter: BrandColors.surfaceSubtle,

    /*
      Màu dựng sẵn của nhãn (Tag color="blue", "green"...). Đổi gốc của chúng sang bảng màu thương
      hiệu để nhãn trạng thái cùng tông với phần còn lại. Các màu phụ (cam, xanh ngọc, tím...) vẫn
      cần cho những trạng thái phải phân biệt được với nhau, nên chỉ dịu bớt cho hợp tông chứ không bỏ.
    */
    blue: BrandColors.blue,
    green: BrandColors.green,
    red: BrandColors.red,
    gold: BrandColors.gold,
    orange: '#D97B29',
    geekblue: '#3A5FB8',
    cyan: '#1A9FB8',
    purple: '#6C55B8',
    magenta: '#B8336A',

    /*
      Bộ sinh bảng màu của thư viện cho xanh lá thương hiệu một nền nhạt ngả xám (#c9d6cd) — đúng thứ
      "nền xám đục" mà quy định thương hiệu cấm. Đặt tay nền và viền nhạt bằng cách pha chính màu xanh
      lá với trắng; chữ và biểu tượng vẫn là màu gốc.
    */
    green1: '#E9F5EF',
    green3: '#B3DAC6',
    colorSuccessBg: '#E9F5EF',
    colorSuccessBorder: '#B3DAC6',

    fontFamily,
    fontSize: 14,
    fontSizeHeading4: 21,
    fontWeightStrong: 600,

    borderRadius: 8,
    borderRadiusLG: 12,
    borderRadiusSM: 6,

    wireframe: false,
  },

  components: {
    Layout: {
      bodyBg: BrandColors.canvas,
      headerBg: BrandColors.white,
      headerHeight: 60,
      headerPadding: '0 24px',
      siderBg: BrandColors.white,
      lightSiderBg: BrandColors.white,
      lightTriggerBg: BrandColors.white,
    },

    Menu: {
      itemBg: 'transparent',
      subMenuItemBg: 'transparent',
      itemColor: BrandColors.inkSecondary,
      itemHoverColor: BrandColors.ink,
      itemHoverBg: BrandColors.surfaceSubtle,
      itemSelectedBg: BrandColors.blueSoft,
      itemSelectedColor: BrandColors.blue,
      subMenuItemSelectedColor: BrandColors.blue,
      itemActiveBg: BrandColors.blueSoft,
      itemBorderRadius: 8,
      itemMarginInline: 12,
      itemMarginBlock: 2,
      itemHeight: 40,
      iconSize: 16,
      activeBarBorderWidth: 0,
    },

    Table: {
      headerBg: BrandColors.surfaceSubtle,
      headerColor: BrandColors.inkSecondary,
      headerSplitColor: 'transparent',
      rowHoverBg: BrandColors.blueMist,
      borderColor: BrandColors.border,
      footerBg: BrandColors.surfaceSubtle,
    },

    Card: {
      headerFontSize: 15,
    },

    Button: {
      fontWeight: 500,
      primaryShadow: 'none',
      defaultShadow: 'none',
      dangerShadow: 'none',
    },

    Tabs: {
      itemColor: BrandColors.inkSecondary,
    },

    Modal: {
      titleFontSize: 17,
    },

    Descriptions: {
      labelBg: BrandColors.surfaceSubtle,
    },

    Statistic: {
      contentFontSize: 26,
    },
  },
};
